/**
 * Utilitaires produit pour la caisse POS (alignés sur le site web).
 */

import {
  computeOptionsExtraFromSelections,
  getMaxSelectableForOption as getMaxSelectableForOptionShared,
  validateOptionsSelectionDetailed,
} from './optionsPricing.js';

export function hasMenuOptions(produit) {
  if (produit?.hasOptions === true) return true;
  return (
    produit?.options?.length > 0 &&
    produit.options.some((opt) => Array.isArray(opt.choix) && opt.choix.length > 0)
  );
}

/** Produit susceptible d'avoir des options (catalogue ou détail). */
export function productMayHaveOptions(produit) {
  if (!produit) return false;
  if (produit.hasOptions === true || (produit.optionsCount ?? 0) > 0) return true;
  if (hasMenuOptions(produit)) return true;
  if (
    produit.prixVariable === true ||
    (produit.prixMin != null &&
      produit.prixMax != null &&
      Number(produit.prixMax) > Number(produit.prixMin))
  ) {
    return true;
  }
  return false;
}

export function isOptionMulti(option) {
  if (option?.type) return option.type === 'CHOIX_MULTIPLE';
  return Boolean(option?.choixMultiple);
}

function isCategorieComptoirAuPoids(categorie) {
  const cat = String(categorie || '').toUpperCase();
  return cat.includes('CHARCUT') || cat.includes('FROMAGE');
}

export function isVenteAuPoids(produit) {
  if (!produit) return false;
  if (produit.venteAuPoids === true) return true;
  if (Number(produit.prixAuPoids) > 0) return true;
  if (isCategorieComptoirAuPoids(produit.categorie)) return true;
  return false;
}

function parsePrixValue(value) {
  if (value == null || value === '') return 0;
  return Number(String(value).replace(',', '.').trim()) || 0;
}

function mergePositiveNumber(fetchedVal, catalogueVal) {
  const f = parsePrixValue(fetchedVal);
  const c = parsePrixValue(catalogueVal);
  if (f > 0) return f;
  if (c > 0) return c;
  const raw = fetchedVal ?? catalogueVal;
  return raw == null || raw === '' ? null : parsePrixValue(raw) || raw;
}

function defaultPortionGrammes(categorie) {
  const cat = String(categorie || '').toUpperCase();
  if (cat.includes('FROMAGE')) return 200;
  if (cat.includes('CHARCUT')) return 50;
  return 100;
}

/** Prix de référence €/kg pour calcul comptoir (prixAuPoids ou prix catalogue). */
export function getPrixKgReference(produit) {
  if (!produit) return 0;
  const auPoids = parsePrixValue(produit.prixAuPoids);
  if (auPoids > 0) return auPoids;

  if (!isVenteAuPoids(produit)) return 0;

  const prixCatalogue = parsePrixValue(produit.prixPromo ?? produit.prix);
  if (prixCatalogue <= 0) return 0;

  if (prixCatalogue >= 20) return prixCatalogue;

  const portion =
    parsePrixValue(produit.poidsPortionStandard) > 0
      ? parsePrixValue(produit.poidsPortionStandard)
      : defaultPortionGrammes(produit.categorie);

  if (portion > 0) {
    return Math.round(((prixCatalogue * 1000) / portion) * 100) / 100;
  }

  return prixCatalogue;
}

/** Libellé prix grille caisse (unitaire ou au poids). */
export function formatPrixProduitCaisse(produit, fmt) {
  if (isVenteAuPoids(produit)) {
    const ref = getPrixKgReference(produit);
    if (ref > 0) {
      const unite = produit.unitePoids || 'kg';
      return `${fmt(ref)}/${unite}`;
    }
    return 'Saisie poids / prix';
  }
  const prix = produit.prixPromo ?? produit.prix;
  if (Number(prix) > 0) {
    return fmt(prix);
  }
  if (
    produit.prixMin != null &&
    produit.prixMax != null &&
    Number(produit.prixMax) > Number(produit.prixMin)
  ) {
    return `${fmt(produit.prixMin)} – ${fmt(produit.prixMax)}`;
  }
  return fmt(0);
}

export function needsAdvancedCustomization(produit) {
  const isComposition =
    produit?.modeComposition === true &&
    ((produit?.taillesPlateaux?.length > 0) || (produit?.elementsComposables?.length > 0));

  return isComposition || produit?.productType === 'PAR_PERSONNE';
}

export function productNeedsCustomization(produit) {
  return hasMenuOptions(produit) || needsAdvancedCustomization(produit);
}

/**
 * Parse CommandeProduit.options (JSON string ou objet) — miroir backend/services/caisseOptionsFormat.
 * Requis pour reprendre un ticket sans perdre les options (POS-L0-D1 / L1-T1).
 */
export function parseOptionsPayload(raw) {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

export function buildLineKey(produitId, optionsPayload) {
  const opts = optionsPayload && Object.keys(optionsPayload).length > 0
    ? JSON.stringify(optionsPayload)
    : '';
  return `${produitId}::${opts}`;
}

export function buildOptionsPayload(produit, quantity, selectedSingleOptions, selectedMultiOptionCounts) {
  const baseOptions = produit?.options || [];
  if (baseOptions.length === 0) return {};

  const payload = {};

  baseOptions.forEach((option) => {
    if (!Array.isArray(option.choix) || option.choix.length === 0) return;

    if (isOptionMulti(option)) {
      const counts = selectedMultiOptionCounts[option.id] || {};
      const items = Object.entries(counts)
        .filter(([, count]) => (Number(count) || 0) > 0)
        .map(([choiceId, count]) => {
          const choix = option.choix.find((c) => c.id === choiceId);
          if (!choix) return null;
          const numericCount = Number(count) || 0;
          return {
            optionId: option.id,
            id: choix.id,
            nom: choix.nom,
            prix: Number(choix.prix) || 0,
            count: numericCount,
            ratio: quantity > 0 ? numericCount / quantity : 0,
          };
        })
        .filter(Boolean);

      if (items.length > 0) {
        payload[option.id] = {
          optionId: option.id,
          nom: option.nom,
          type: 'multi',
          choixMultiple: true,
          choixMinimum: option.choixMinimum,
          choixMaximum: option.choixMaximum,
          nbInclus: option.nbInclus,
          obligatoire: option.obligatoire,
          baseQuantity: quantity,
          choix: items,
        };
      }
    } else {
      const selectedId = selectedSingleOptions[option.id];
      if (!selectedId) return;
      const choix = option.choix.find((c) => c.id === selectedId);
      if (!choix) return;

      payload[option.id] = {
        optionId: option.id,
        nom: option.nom,
        type: 'single',
        choixMultiple: false,
        choixMinimum: option.choixMinimum,
        choixMaximum: option.choixMaximum,
        nbInclus: option.nbInclus,
        obligatoire: option.obligatoire,
        baseQuantity: quantity,
        choix: [
          {
            optionId: option.id,
            id: choix.id,
            nom: choix.nom,
            prix: Number(choix.prix) || 0,
            count: quantity,
            ratio: quantity > 0 ? 1 : 0,
          },
        ],
      };
    }
  });

  return payload;
}

export function computeOptionsExtraPrice(optionsPayload) {
  if (!optionsPayload || typeof optionsPayload !== 'object') return 0;
  let total = 0;
  Object.values(optionsPayload).forEach((group) => {
    if (!group?.choix) return;
    group.choix.forEach((c) => {
      total += (Number(c.prix) || 0) * (Number(c.count) || 1);
    });
  });
  return total;
}

/**
 * Recalcule le supplément depuis les définitions produit (nbInclus).
 */
export function computeOptionsExtraPriceForProduit(
  produit,
  selectedSingleOptions,
  selectedMultiOptionCounts,
  productQty = 1
) {
  return computeOptionsExtraFromSelections(
    produit,
    selectedSingleOptions,
    selectedMultiOptionCounts,
    productQty
  );
}

/**
 * Lignes structurées pour affichage panier / ticket (ex. Taille → Grand).
 * @returns {{ group: string, choices: string }[]}
 */
export function formatOptionsLines(optionsPayload) {
  if (!optionsPayload || typeof optionsPayload !== 'object') return [];

  const lines = [];
  Object.values(optionsPayload).forEach((group) => {
    if (!group?.choix?.length) return;
    const groupName = (group.nom || 'Option').trim();
    const choices = group.choix
      .map((c) => {
        const nom = (c.nom || '').trim();
        const n = Number(c.count) || 1;
        return n > 1 ? `${nom} ×${n}` : nom;
      })
      .filter(Boolean)
      .join(', ');
    if (choices) {
      lines.push({ group: groupName, choices });
    }
  });
  return lines;
}

/** Résumé une ligne (ticket compact) : « Taille : Grand · Parfums : Myrtille » */
export function formatOptionsSummary(optionsPayload) {
  return formatOptionsLines(optionsPayload)
    .map(({ group, choices }) => `${group} : ${choices}`)
    .join(' · ');
}

/** Bloc multi-lignes pour commentaire / impression */
export function formatOptionsTicketBlock(optionsPayload) {
  return formatOptionsLines(optionsPayload)
    .map(({ group, choices }) => `${group} : ${choices}`)
    .join('\n');
}

export function getDefaultOptionSelections(produit) {
  return { defaultSingles: {}, defaultMulti: {} };
}

/** Fusionne catalogue + réponse API sans écraser les options par un tableau vide. */
export function mergeProduitAvecOptions(catalogueItem, fetched) {
  const fromFetched = (fetched?.options || []).filter(
    (o) => Array.isArray(o.choix) && o.choix.length > 0
  );
  const fromCatalogue = (catalogueItem?.options || []).filter(
    (o) => Array.isArray(o.choix) && o.choix.length > 0
  );
  const options = fromFetched.length > 0 ? fromFetched : fromCatalogue;
  const prixAuPoidsMerged = mergePositiveNumber(fetched?.prixAuPoids, catalogueItem?.prixAuPoids);
  const prixMerged = mergePositiveNumber(fetched?.prix, catalogueItem?.prix);
  const prixPromoMerged = mergePositiveNumber(fetched?.prixPromo, catalogueItem?.prixPromo);
  const poidsPortionMerged = mergePositiveNumber(
    fetched?.poidsPortionStandard,
    catalogueItem?.poidsPortionStandard
  );
  return {
    ...catalogueItem,
    ...(fetched || {}),
    venteAuPoids: fetched?.venteAuPoids ?? catalogueItem?.venteAuPoids,
    prixAuPoids: prixAuPoidsMerged,
    prix: prixMerged,
    prixPromo: prixPromoMerged,
    unitePoids: fetched?.unitePoids ?? catalogueItem?.unitePoids,
    poidsPortionStandard: poidsPortionMerged,
    decoupesDisponibles:
      fetched?.decoupesDisponibles ?? catalogueItem?.decoupesDisponibles,
    options,
    hasOptions: options.length > 0,
    optionsCount: options.length,
  };
}

export function validateOptionsSelection(produit, selectedSingleOptions, selectedMultiOptionCounts) {
  return validateOptionsSelectionDetailed(
    produit,
    selectedSingleOptions,
    selectedMultiOptionCounts
  ).map((e) => e.option);
}

export { getMaxSelectableForOptionShared as getMaxSelectableForOption };
